import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { BASE_URL } from '../api/api-сonfig';

interface AuthContextType {
  token: string | null;
  isCheckingSession: boolean;
  setToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'token';
const LEGACY_EXPIRATION_STORAGE_KEY = 'tokenExpiration';
const SESSION_VALIDATION_TIMEOUT_MS = 10_000;

const clearStoredSession = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  // This value was trusted by older versions of the app. It is deliberately
  // removed because only the server may decide whether a session is valid.
  localStorage.removeItem(LEGACY_EXPIRATION_STORAGE_KEY);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setTokenState] = useState<string | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!savedToken) {
      clearStoredSession();
      setIsCheckingSession(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      SESSION_VALIDATION_TIMEOUT_MS
    );
    let isActive = true;

    const validateStoredSession = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/wp-json/jwt-auth/v1/token/validate`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${savedToken}`,
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Session validation failed with ${response.status}`);
        }

        if (isActive) {
          setTokenState(savedToken);
        }
      } catch {
        if (isActive) {
          clearStoredSession();
          setTokenState(null);
        }
      } finally {
        window.clearTimeout(timeoutId);
        if (isActive) {
          setIsCheckingSession(false);
        }
      }
    };

    validateStoredSession();

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const handleSetToken = (newToken: string | null) => {
    // A token is accepted here only after the login endpoint has returned it.
    // Tokens restored from browser storage always go through server validation.
    if (newToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
      localStorage.removeItem(LEGACY_EXPIRATION_STORAGE_KEY);
    } else {
      clearStoredSession();
    }

    setTokenState(newToken);
    setIsCheckingSession(false);
  };

  return (
    <AuthContext.Provider
      value={{ token, isCheckingSession, setToken: handleSetToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth должен использоваться в AuthProvider');
  }
  return context;
};
